/**
 * Golem fork (FT-1398): THE NIGHT LAB — preview any role's night art in-app,
 * no game required.
 *
 * WHAT THIS IS. A client-local preview room: eight fake seats are seeded into
 * the REAL players store, the viewer is seated as one of them, and every
 * night state is driven through the SAME store commits the live flow makes —
 * `night/stagePick` when a coin is tapped (the real Player.vue handler, not a
 * copy), `night/playerAction` + `night/confirmStage` for the seal (NightCall
 * .confirm's own pair), `night/setPlayerNight` for the storyteller's frames
 * (fabricated to exactly the shape the socket layer would commit on arrival —
 * built with golem/nightLog's own makeEntry, so the wire shape and the lab
 * shape cannot drift). There is no parallel render mode anywhere: the ring,
 * the face, the threads and the marks are the production components reading
 * the production getters.
 *
 * ── STRICTLY LOCAL — the belt AND the braces ─────────────────────────────
 *
 * THE BELT: the lab only opens while this client is NOT in a town
 * (`session.sessionId` empty — `canEnter`). The socket only ever connects on
 * `session/setSessionId` (socket.js's subscriber), the lab never commits it,
 * and every LiveSession sender no-ops with no socket (`_send` requires an
 * OPEN socket). A client that has never connected physically cannot leak.
 * FT-1501 (user: "Give L.O.T.S. the same character preview"): the door that
 * opens the lab is the Lot editor's live preview — the only one (the golem
 * menu's Night lab row went with FT-1497, its strip with FT-1501). In a live
 * town the editor says so rather than exiting the session first — the safer
 * of the two shapes: no destructive side effect can hide inside a preview.
 *
 * THE BRACES: the mutation audit, the FT-1173 staged-deaths precedent
 * (absence from socket.js's subscription table IS the privacy mechanism).
 * Every mutation the lab commits, against that table:
 *
 *   NOT IN THE TABLE (nothing would send even in a live town):
 *     night/setPlayerNight · night/settleTold · night/stagePick ·
 *     night/confirmStage · night/resetStage · session/setSpectator ·
 *     session/setPlayerId
 *   IN THE TABLE, and what its sender does disconnected:
 *     players/set|add|update → sendGamestate/sendPlayer — spectator-guarded
 *       AND `_send` is a no-op with no socket;
 *     session/claimSeat → claimSeat() — `_send`, no socket, no-op;
 *     toggleNight → setIsNight (spectator-guarded) + systemMessage
 *       (storyteller-only) + automations (host-only, and every automation
 *       flag defaults false; flags are only ever set by a relay frame);
 *     night/setDay|setMode|setLog|addEntry|patchEntry|removeEntry →
 *       sendNightRows — host-guarded on the LIVESESSION's own `_isSpectator`,
 *       which starts true and is only refreshed by connect(); `_send` no-op
 *       regardless;
 *     night/playerAction → sendNightAction — `_sendDirect` → `_send`, no
 *       socket, no-op;
 *
 * ── EXIT CLEANLINESS (the FT-1389 class) ─────────────────────────────────
 *
 * Entering snapshots every store slice the lab touches AND the raw
 * localStorage keys the persistence plugin writes on the lab's commits
 * (players, playerId, the night-log stash…). Exiting restores the slices
 * through ordinary commits — spectator is restored LAST so the persistence
 * plugin's host-only writers stay off throughout — and then writes the raw
 * localStorage values back byte-for-byte, so even the intermediate saves the
 * plugin made during the lab leave no trace. The told stamps are swept by
 * re-delivering every row the lab ever committed as unsent (the same reopen
 * sweep a real retraction rides) before the pre-entry playerNight frame is
 * restored.
 *
 * ── TRUTH OVER FLATTERY ──────────────────────────────────────────────────
 *
 * A role with no bespoke dress shows its real fallback (the plain invite
 * ring) — that IS what a player of that role sees tonight, and the lab's
 * job is to say so. The FT-1397 family tier plugs in with zero lab changes.
 *
 * ── NIGHT TEXT (FT-1486 → FT-1501) ───────────────────────────────────────
 *
 * A Lot's night text lives on the Lot (its library row's `night.copy`). The
 * lab never writes the edition; while the Lot editor previews, its night
 * text on the table edits in place (NightCopyText) through the editor's own
 * door (`nightLab.copy`, below).
 */
import Vue from "vue";
import rolesJSON from "../roles.json";
import editionJSON from "../editions.json";
import { makeEntry, entryId, targetCount, reminderFor } from "./nightLog";
import { TOLD_ROLES, toldSentence } from "./toldInfo";
import {
  fieldsFor,
  playerSlots,
  playerCharSlots,
  extraFields,
  labelFor,
  nightExchange,
  NIGHT_EXCHANGE,
  FIELD_TYPES,
  FIELD_OWNERS,
} from "./nightInfo";
import {
  copyIsBlank,
  defaultCopyFor,
  FACE_KEYS,
  overrideFor,
} from "./nightSigns";

/** The fake town: eight varied names, the viewer's chair, a bystander's. */
export const LAB_SEATS = [
  "Maeve",
  "Bram",
  "Odette",
  "Silas",
  "Petra",
  "Ines",
  "Corwin",
  "Talia",
];
export const ACTOR_SEAT = 0;
export const BYSTANDER_SEAT = 4;
const LAB_IDS = LAB_SEATS.map(
  (n, i) => "nightlab-" + i + "-" + n.toLowerCase(),
);

/** The raw keys the persistence plugin can write while the lab runs — all
 *  snapshotted at entry and restored byte-for-byte at exit. */
const LS_KEYS = [
  "players",
  "playerId",
  "session",
  "bluffs",
  "fabled",
  "gameEnded",
  "golem.nightLog",
  "golem.nightMode",
  "golem.nightRequireChecks",
];

/** The two grammars (FT-1384 choosers, FT-1385 told roles), one state id per
 *  beat. RECEIVE is the chooser grammar minus the picks — a role that acts
 *  without pointing (free-text, or receive-only with no slots). */
const CHOOSER_GRAMMAR = ["invite", "staged", "sealed", "st-sent", "reopened"];
const TOLD_GRAMMAR = ["before", "telling", "settled", "residue"];
const RECEIVE_GRAMMAR = ["invite", "st-sent", "reopened"];

export const STATE_LABELS = {
  invite: "Invitation",
  staged: "Staged",
  sealed: "Sealed",
  "st-sent": "ST sent",
  reopened: "Reopened",
  before: "Before",
  telling: "Telling",
  settled: "Settled",
  residue: "Residue",
};

export const PERSPECTIVES = [
  { id: "acting", label: "The player" },
  { id: "bystander", label: "A bystander" },
  { id: "storyteller", label: "The storyteller" },
];

/** The lab's one piece of reactive state — the editor's stage bar renders
 *  from this. */
export const nightLab = Vue.observable({
  open: false,
  roleId: "",
  stateId: "",
  perspective: "acting",
  // FT-1501 (Clocktower's FT-1475): who is driving — true for the Lot
  // editor's preview (its stage bar carries the controls). The only door
  // left, but kept a flag so a future second door reads the same way.
  studio: false,
  // FT-1501 (Clocktower's FT-1497): THE EDITOR'S NIGHT-TEXT DOOR — handed in
  // by the Lot editor at enter (frozen, so Vue never walks it) and dropped at
  // exit. While it stands, the table's night text for the lab's Lot is
  // click-to-edit (NightCopyText); every edit goes through it into the
  // editor's form.
  //   raw(key)          the form's words for one copy key — null = the
  //                     default, "" = left empty on purpose (FT-1536)
  //   write(key, text)  set them (null puts the default back)
  //   fallback(key)     the shipped default the empty words stand for
  //   max               the copy layer's cap (COPY_MAX)
  copy: null,
});

/** Entry snapshot + the ids of every host frame the lab ever fabricated
 *  (the exit sweep re-delivers each as unsent to clear its told stamp). */
let snap = null;
// FT-1493 → FT-1501: how the preview's OWNER stops it, handed in on enter.
// The menu's Exit preview asks through stopPreview, so the editor's preview
// also stands its own stage bar down.
let stopper = null;
const labRowIds = new Set();

const roleById = new Map(rolesJSON.map((r) => [r.id, r]));

/**
 * FT-1501 (Clocktower's FT-1475): roles the lab may show that are NOT
 * bundled — the Lot editor hands in the custom Lot on its bench (a script's
 * own Lot, or the draft of a new one) as a whole object at enter(). Cleared
 * at exit. Every lookup below goes through `labRole`, so a bundled id always
 * wins.
 */
const extraRoles = new Map();
function labRole(id) {
  return roleById.get(id) || extraRoles.get(id) || null;
}

/** Does this character act at night at all — the picker's population rule. */
const actsAtNight = (r) => r.firstNight > 0 || r.otherNight > 0;

/**
 * The role picker's groups: every edition that has night-acting characters,
 * then the travellers (any edition) who wake. Group names come from
 * editions.json where the id is known there.
 */
export function roleGroups() {
  const names = new Map(editionJSON.map((e) => [e.id, e.name]));
  const groups = [];
  const seen = new Set();
  editionJSON.forEach((ed) => {
    const roles = rolesJSON.filter(
      (r) => r.edition === ed.id && actsAtNight(r) && r.team !== "traveler",
    );
    if (!roles.length) return;
    roles.forEach((r) => seen.add(r.id));
    groups.push({ id: ed.id, name: names.get(ed.id) || ed.id, roles });
  });
  const rest = rolesJSON.filter(
    (r) => actsAtNight(r) && r.team !== "traveler" && !seen.has(r.id),
  );
  if (rest.length)
    groups.push({ id: "other", name: "Other scripts", roles: rest });
  const travellers = rolesJSON.filter(
    (r) => r.team === "traveler" && actsAtNight(r),
  );
  if (travellers.length) {
    groups.push({ id: "travellers", name: "Travellers", roles: travellers });
  }
  return groups;
}

/** Which grammar a role speaks. */
export function grammarOf(roleId) {
  if (TOLD_ROLES[roleId]) return TOLD_GRAMMAR;
  return picksAnything(roleId) ? CHOOSER_GRAMMAR : RECEIVE_GRAMMAR;
}

/** FT-1532 (Clocktower's char-pick half, ported FT-1538): does this Lot's
 *  player pick anything — a seat or (FT-1475) a character? Such a Lot walks
 *  the whole chooser grammar: a character pick is staged, confirmed and
 *  answered exactly like a seat. */
function picksAnything(roleId) {
  return playerSlots(roleId) > 0 || playerCharSlots(roleId) > 0;
}

/**
 * The lab may only open while this client is not in a town — the belt — and
 * never over a lab already running.
 *
 * FT-1497 (user): the FT-1403 owner gate (`canUseLab` — the superuser tier or
 * the devLabs browser flag) is gone with the menu row it gated. The only door
 * left is the Lot editor's preview (FT-1501), which opens for whoever can
 * edit the Lot on its bench; the lab is client-local either way, so WHO may
 * look is the editor's question and this stays the safety belt alone.
 */
export function canEnter(store) {
  return !store.state.session.sessionId && !nightLab.open;
}

/** The night this role is previewed on: its first night where it has one,
 *  otherwise a later night (the Monk's, the Imp's). */
function labDayOf(role) {
  return role && role.firstNight > 0 ? 1 : 2;
}

const actor = (store) => store.state.players.players[ACTOR_SEAT];

/** Night on/off through the ONE mutation every real path uses; the day is
 *  re-asserted after (toggleNight's own increment is the live game's rule,
 *  not the lab's). */
function ensureNight(store, on, role) {
  if (store.state.grimoire.isNight !== !!on) {
    store.commit("toggleNight", !!on);
  }
  if (on) {
    const day = labDayOf(role || labRole(nightLab.roleId));
    if (store.state.night.day !== day) store.commit("night/setDay", day);
  }
}

/** Canned picks for a chooser driven from the stepper (a tap on the ring is
 *  the first-class way in — this is only the default when none was staged).
 *  Seats chosen away from the actor so the strings read across the ring. */
function cannedPicks(slots) {
  return [3, 6, 5].slice(0, slots);
}

/** FT-1532 (ported FT-1538): canned CHARACTER picks for a Lot whose player
 *  picks characters (the seats' twin above). L.O.T.S. adaptation: three of
 *  its own bundled Lots — Clocktower's canned characters are not Lots. */
function cannedCharacters(slots) {
  return ["mask", "compass", "butterfly"].slice(0, slots).map((id) => {
    const r = roleById.get(id);
    return { id, name: (r && r.name) || id };
  });
}

/** The canned storyteller answer for a chooser's ST-sent frame — one value
 *  per storyteller field the role's own schema declares, in the shape the
 *  night sheet itself would record. */
function cannedAnswer(roleId) {
  const { fields, known } = fieldsFor(roleId);
  const told = {};
  if (!known) return told;
  fields.forEach((f) => {
    if (f.by !== FIELD_OWNERS.STORYTELLER) return;
    if (f.type === FIELD_TYPES.BOOLEAN) told.ping = true;
    else if (f.type === FIELD_TYPES.NUMBER) told.number = 1;
    else if (f.type === FIELD_TYPES.CHARACTER) {
      told.characterId = "poisoner";
      told.characterName = "Poisoner";
    }
  });
  return told;
}

/** The canned telling for a told-information role (FT-1385's five, plus
 *  FT-1402's Grandmother and FT-1412's Undertaker). */
function cannedTelling(roleId) {
  const spec = TOLD_ROLES[roleId];
  if (!spec) return { targets: [], told: {} };
  // character (the Undertaker): one plausible execution — no seat, just the
  // character the town buried today
  if (spec.kind === "character") {
    return {
      targets: [],
      told: { characterId: "poisoner", characterName: "Poisoner" },
    };
  }
  // identity (the Grandmother): one plausible grandchild — a good player
  // across the ring and a Townsfolk character
  if (spec.kind === "identity") {
    return {
      targets: [3],
      told: { characterId: "chef", characterName: "Chef" },
    };
  }
  if (spec.kind === "pair") {
    const name =
      spec.group === "Townsfolk"
        ? { characterId: "chef", characterName: "Chef" }
        : spec.group === "Outsider"
        ? { characterId: "butler", characterName: "Butler" }
        : { characterId: "poisoner", characterName: "Poisoner" };
    return { targets: [3, 6], told: name };
  }
  return { targets: [], told: { number: 1 } };
}

/**
 * Fabricate the host frame for the actor's row — the exact store shape the
 * socket layer's "night" frame handler commits (night/setPlayerNight
 * re-projects through projectPlayerRow on the way in, so committing the full
 * entry here IS the delivered shape). Built with makeEntry so every key the
 * live row carries exists here too.
 */
function hostFrame(
  store,
  { targets = [], characters = [], told = {}, done = true } = {},
) {
  const role = labRole(nightLab.roleId);
  const day = labDayOf(role);
  const seats = store.state.players.players;
  const first = day <= 1;
  const slots = Math.max(targetCount(role, first), targets.length);
  const e = makeEntry({
    day,
    seat: ACTOR_SEAT,
    seatName: LAB_SEATS[ACTOR_SEAT],
    playerId: LAB_IDS[ACTOR_SEAT],
    roleId: role.id,
    roleName: role.name,
    trueRoleId: role.id,
    trueRoleName: role.name,
    shownRoleId: role.id,
    shownRoleName: role.name,
    isPerformance: false,
    order: 1,
    slots,
  });
  targets.forEach((t, i) => {
    e.targets[i] = t;
    e.targetNames[i] = (seats[t] && seats[t].name) || "";
    e.targetsBy[i] = "player";
  });
  // FT-1532 (ported FT-1538): the player's own character picks, echoed as the
  // host would
  e.playerCharacters = characters
    .filter((c) => c && c.id)
    .map((c) => ({ id: c.id, name: c.name || "" }));
  e.told = { ...e.told, ...told };
  e.done = !!done;
  labRowIds.add(e.id);
  return e;
}

/** Deliver one host frame (or none) to this client — the socket's commit. */
function deliver(store, rows) {
  store.commit("night/setPlayerNight", { live: true, rows });
}

/** Make sure the actor has a complete STAGED pick (the ring's own commits —
 *  a coin already tapped by hand is kept; empty slots take canned seats). */
function ensureStaged(store) {
  ensureStagedCharacters(store);
  const slots = playerSlots(nightLab.roleId);
  if (!slots) return [];
  const staged = store.getters["night/myStagedTargets"];
  const wanted = cannedPicks(slots);
  const have = new Set(staged.filter((t) => Number.isInteger(t) && t >= 0));
  let need = slots - have.size;
  for (const seat of wanted) {
    if (need <= 0) break;
    if (have.has(seat)) continue;
    store.commit("night/stagePick", {
      day: store.state.night.day,
      roleId: nightLab.roleId,
      slots,
      seat,
    });
    have.add(seat);
    need--;
  }
  return store.getters["night/myStagedTargets"];
}

/** FT-1532 (ported FT-1538): the character twin of ensureStaged — every empty
 *  character slot takes a canned character (a pick already made by hand is
 *  kept). */
function ensureStagedCharacters(store) {
  const slots = playerCharSlots(nightLab.roleId);
  if (!slots) return [];
  const canned = cannedCharacters(slots);
  for (let i = 0; i < slots; i++) {
    const cur = store.getters["night/myStagedCharacters"][i];
    if (cur && cur.id) continue;
    store.commit("night/stageCharacter", {
      day: store.state.night.day,
      roleId: nightLab.roleId,
      slots,
      index: i,
      pick: canned[i] || canned[0],
    });
  }
  return store.getters["night/myStagedCharacters"];
}

/** FT-1532 (ported FT-1538): NightCall.confirm's commits, for whatever the
 *  Lot picks — the wire event carrying the seats and the characters, then
 *  the local seal. */
function confirmPicks(store, role, targets) {
  const slots = playerSlots(role.id);
  const payload = { roleId: role.id };
  if (slots) payload.targets = targets.slice(0, slots);
  if (playerCharSlots(role.id)) {
    payload.characters = store.getters["night/myStagedCharacters"].map((c) => ({
      id: c.id,
    }));
  }
  store.commit("night/playerAction", payload);
  if (slots) store.commit("night/confirmStage");
  else {
    store.commit("night/sealStage", {
      day: store.state.night.day,
      roleId: role.id,
    });
  }
}

/**
 * The storyteller's own ledger, kept true to the current state while the
 * storyteller perspective is up — written through night/write (the sheet's
 * one write path) so the roster row and the entry match the live app's.
 */
function syncEntries(store) {
  if (nightLab.perspective !== "storyteller") return;
  const role = labRole(nightLab.roleId);
  if (!role) return;
  const day = store.state.night.day;
  const id = entryId(day, ACTOR_SEAT, role.id);
  const state = nightLab.stateId;
  // states where the host has heard nothing: no row at all
  const silent = state === "invite" || state === "staged" || state === "before";
  if (silent) {
    if (store.state.night.entries.some((e) => e.id === id)) {
      store.commit("night/removeEntry", id);
    }
    return;
  }
  const row = store.getters["night/roster"].find(
    (r) => r.seat === ACTOR_SEAT && r.role.id === role.id,
  );
  if (!row) return;
  const seats = store.state.players.players;
  let patch;
  if (TOLD_ROLES[role.id]) {
    const { targets, told } = cannedTelling(role.id);
    patch = {
      targets: targets.slice(),
      targetNames: targets.map((t) => (seats[t] && seats[t].name) || ""),
      targetsBy: targets.map(() => ""),
      told: {
        ping: null,
        number: null,
        characterId: "",
        characterName: "",
        text: "",
        ...told,
      },
      done: true,
    };
  } else {
    // the actor's OWN staged pick, read off the raw stage rather than the
    // getter — the getter answers through myCall, which is null in the
    // storyteller perspective, and the ledger must carry what the player
    // actually pointed at, not the canned fallback
    const st = store.state.night.staging;
    const staged =
      st.roleId === role.id && st.targets.some((t) => t >= 0) ? st.targets : [];
    const picks = staged.length ? staged : cannedPicks(playerSlots(role.id));
    patch = {
      targets: picks.slice(),
      targetNames: picks.map((t) =>
        t >= 0 ? (seats[t] && seats[t].name) || "" : "",
      ),
      targetsBy: picks.map((t) => (t >= 0 ? "player" : "")),
      // FT-1532 (ported FT-1538): and the characters the player picked,
      // where they pick any
      ...(playerCharSlots(role.id)
        ? {
            playerCharacters: (st.roleId === role.id &&
            (st.characters || []).some((c) => c && c.id)
              ? st.characters
              : cannedCharacters(playerCharSlots(role.id))
            ).map((c) => ({ id: c.id, name: c.name })),
          }
        : {}),
      told: {
        ping: null,
        number: null,
        characterId: "",
        characterName: "",
        text: "",
        ...(state === "st-sent" ? cannedAnswer(role.id) : {}),
      },
      done:
        state === "st-sent" ||
        state === "telling" ||
        state === "settled" ||
        state === "residue",
    };
  }
  store.dispatch("night/write", { row, patch });
  labRowIds.add(id);
}

/**
 * Drive the town to one state of the current role's grammar — the same
 * commits the live flow makes, in the live flow's order. Stepping BACKWARD
 * retracts the later beats the way the live flow retracts them (the unsent
 * re-delivery — the reopen sweep — is the one wire shape a retraction has).
 *
 * In the bystander and storyteller perspectives only the phase (and the
 * storyteller's ledger) is kept true — the actor-side commits are the acting
 * seat's own and re-run whole when that chair is retaken (setPerspective).
 */
export function applyState(store, stateId) {
  if (!nightLab.open) return;
  const role = labRole(nightLab.roleId);
  if (!role) return;
  nightLab.stateId = stateId;
  if (nightLab.perspective !== "acting") {
    ensureNight(store, stateId !== "residue", role);
    syncEntries(store);
    return;
  }
  switch (stateId) {
    // ── the chooser grammar (FT-1384) ────────────────────────────────────
    case "invite":
      ensureNight(store, true, role);
      sweepLabRows(store);
      store.commit("night/resetStage");
      break;
    case "staged":
      ensureNight(store, true, role);
      sweepLabRows(store);
      // a seal left standing by a backward step: the sweep only unseals
      // where a SENT row was retracted, so clear the rest by hand
      if (store.getters["night/myConfirmed"]) {
        store.commit("night/resetStage");
      }
      ensureStaged(store);
      break;
    case "sealed": {
      ensureNight(store, true, role);
      sweepLabRows(store);
      const staged = ensureStaged(store);
      // NightCall.confirm's own two commits, same order: the wire event,
      // then the local seal. FT-1532 (ported FT-1538): character picks ride
      // it too.
      confirmPicks(store, role, staged);
      break;
    }
    case "st-sent": {
      ensureNight(store, true, role);
      let picks = [];
      let characters = [];
      // FT-1532 (ported FT-1538): a character-picking Lot confirms its picks
      // here too
      if (picksAnything(role.id)) {
        picks = ensureStaged(store);
        characters = store.getters["night/myStagedCharacters"].slice();
        if (!store.getters["night/myConfirmed"]) {
          confirmPicks(store, role, picks);
        }
      }
      deliver(store, [
        hostFrame(store, {
          targets: picks.filter((t) => Number.isInteger(t) && t >= 0),
          characters,
          told: cannedAnswer(role.id),
          done: true,
        }),
      ]);
      break;
    }
    case "reopened": {
      ensureNight(store, true, role);
      // the Send button's other job: the same row arrives unsent (targets
      // kept, exactly as a real reopen keeps them), and the reopen sweep
      // (night/setPlayerNight) unseals the stage.
      const staged = store.getters["night/myStagedTargets"];
      deliver(store, [
        hostFrame(store, {
          targets: staged.filter((t) => Number.isInteger(t) && t >= 0),
          told: {},
          done: false,
        }),
      ]);
      // reached without a send standing (a sideways jump): the sweep had no
      // sent row to watch, so the unseal is finished by hand
      if (store.getters["night/myConfirmed"]) {
        store.commit("night/resetStage");
      }
      break;
    }
    // ── the told grammar (FT-1385) ───────────────────────────────────────
    case "before":
      ensureNight(store, true, role);
      sweepLabRows(store);
      break;
    case "telling": {
      ensureNight(store, true, role);
      // sweep first: a settled mark from an earlier step would otherwise
      // dress the fresh arrival in the residue pose
      sweepLabRows(store);
      const { targets, told } = cannedTelling(role.id);
      deliver(store, [hostFrame(store, { targets, told, done: true })]);
      break;
    }
    case "settled": {
      ensureNight(store, true, role);
      if (!store.getters["night/myTold"]) {
        const { targets, told } = cannedTelling(role.id);
        deliver(store, [hostFrame(store, { targets, told, done: true })]);
      }
      // the socket plugin's own settle commit, fired by hand instead of by
      // its TOLD_HOLD_MS timer — the lab holds states still.
      const told = store.getters["night/myTold"];
      if (told) store.commit("night/settleTold", told.rowId);
      break;
    }
    case "residue": {
      if (!store.getters["night/myTold"]) {
        ensureNight(store, true, role);
        const { targets, told } = cannedTelling(role.id);
        deliver(store, [hostFrame(store, { targets, told, done: true })]);
      }
      const told = store.getters["night/myTold"];
      if (told && told.phase !== "settled") {
        store.commit("night/settleTold", told.rowId);
      }
      ensureNight(store, false, role);
      break;
    }
  }
  syncEntries(store);
}

/** Pick a role: clear the last role's night, dress the actor's seat, land on
 *  the grammar's first state. */
export function setRole(store, roleId) {
  if (!nightLab.open || !labRole(roleId)) return;
  const role = labRole(roleId);
  // retract whatever the last role delivered (unsent stubs ride the reopen
  // sweep, clearing any told stamps), then start clean — the storyteller's
  // ledger rows the lab wrote go with it
  sweepLabRows(store);
  labRowIds.forEach((id) => {
    if (store.state.night.entries.some((e) => e.id === id)) {
      store.commit("night/removeEntry", id);
    }
  });
  labRowIds.clear();
  store.commit("night/resetStage");
  nightLab.roleId = roleId;
  store.commit("players/update", {
    player: actor(store),
    property: "role",
    value: role,
  });
  applyState(store, grammarOf(roleId)[0]);
}

/**
 * FT-1501 (Clocktower's FT-1475): the Lot editor's live edits on the acting
 * seat — the draft's name and abilities laid over the role the seat wears,
 * through the same `players/update` setRole uses (the actor is a fake seat;
 * its object is the lab's). Every surface that reads the seat's role
 * repaints; the role's id — and so its night grammar — is untouched.
 */
export function patchActorRole(store, fields) {
  if (!nightLab.open) return;
  const role = labRole(nightLab.roleId);
  const seat = actor(store);
  if (!role || !seat) return;
  store.commit("players/update", {
    player: seat,
    property: "role",
    value: Object.assign({}, role, fields),
  });
}

/** Every row the lab ever delivered, re-delivered unsent — the reopen sweep
 *  clears their stamps/settled marks — then an empty frame. */
function sweepLabRows(store) {
  if (labRowIds.size) {
    deliver(
      store,
      [...labRowIds].map((id) => ({ id, sent: false })),
    );
  }
  deliver(store, []);
}

/** Swap whose eyes the town is seen through. */
export function setPerspective(store, p) {
  if (!nightLab.open || nightLab.perspective === p) return;
  nightLab.perspective = p;
  if (p === "storyteller") {
    store.commit("session/claimSeat", -1);
    store.commit("session/setSpectator", false);
    syncEntries(store);
    return;
  }
  store.commit("session/setSpectator", true);
  const seat = p === "bystander" ? BYSTANDER_SEAT : ACTOR_SEAT;
  store.commit("session/setPlayerId", LAB_IDS[seat]);
  store.commit("session/claimSeat", seat);
  if (p === "bystander") {
    // a bystander's client holds NO rows — that is the FT-1384/1385 privacy
    // fact this perspective exists to show. (Stamps survive in the client's
    // own bookkeeping, so stepping back re-renders the same phase.)
    deliver(store, []);
  } else {
    // back in the actor's chair: re-deliver the current state's frames
    applyState(store, nightLab.stateId);
  }
}

/** Re-fire the current state's arrival: step back one beat, then forward. */
export function replay(store) {
  if (!nightLab.open) return;
  const g = grammarOf(nightLab.roleId);
  const i = g.indexOf(nightLab.stateId);
  const current = nightLab.stateId;
  applyState(store, g[Math.max(0, i - 1)]);
  Vue.nextTick(() => {
    setTimeout(() => {
      if (nightLab.open) applyState(store, current);
    }, 80);
  });
}

// ─── WHICH WORDS A ROLE SPEAKS (the Lot editor's stepper dots read these) ──

/**
 * FT-1410: does this role's night-sheet row speak BOTH halves — seat pickers
 * AND a told control? The same question NightSheet's `splitLabels` asks, put
 * to the same two sources (nightLog's slot count, nightInfo's told fields on
 * the night the lab is showing).
 */
function rowSplits(roleId) {
  const role = labRole(roleId);
  if (!role) return false;
  const first = labDayOf(role) <= 1;
  return (
    targetCount(role, first) > 0 && extraFields(roleId, first).fields.length > 0
  );
}

/** Which copy keys can actually RENDER for this role. A told role speaks its
 *  two sentences; a chooser with seats of its own speaks the verb pair; a
 *  role that points at nobody has only the sheet line (its ask never draws
 *  one — NightCall gates the ask on `action.slots`). FT-1410: and a role
 *  whose SHEET ROW splits also owns that row's two labels. */
export function copyKeysFor(roleId) {
  if (!roleId) return [];
  // FT-1532 (Clocktower's, ported FT-1535): a chooser speaks its face per
  // state — the ask and the white line of each of the four — and keeps
  // `line` for the storyteller's sheet
  const base = TOLD_ROLES[roleId]
    ? ["telling", "settled", "line"]
    : picksAnything(roleId)
    ? Object.values(FACE_KEYS)
        .reduce((out, k) => out.concat([k.ask, k.line]), [])
        .concat(["line"])
    : ["line"];
  return rowSplits(roleId) ? base.concat(["seats", "told"]) : base;
}

/**
 * FT-1497: the ONE label a single-half sheet row wears — asked the way
 * NightSheet's `soloLabel` asks it (an authored word under the key naming the
 * slot the row draws, else the role's own verb) — as that key, or "" where
 * the row splits or wears no label at all.
 */
function soloLabelKey(roleId) {
  const role = labRole(roleId);
  if (!role || rowSplits(roleId)) return "";
  const key = targetCount(role, labDayOf(role) <= 1) > 0 ? "seats" : "told";
  return overrideFor(roleId, key) || labelFor(roleId) ? key : "";
}

/**
 * FT-1497: WHERE THIS ROLE'S NIGHT TEXT SHOWS — the copy keys the table
 * renders in one viewpoint and state (the Lot editor's stepper dots, so no
 * editable line is missed). Read off the same facts the surfaces render
 * from: a bystander is shown nothing; the storyteller's row exists only once
 * the host has heard something (syncEntries' silent beats) and carries the
 * line and its label(s); the player's face carries the line through the
 * night, the invite while it asks (and again once reopened), the seal once
 * sealed, and a told role's two sentences one beat each.
 */
export function copyKeysShown(roleId, perspective, stateId) {
  if (!roleId || perspective === "bystander") return [];
  const keys = copyKeysFor(roleId);
  if (perspective === "storyteller") {
    if (stateId === "invite" || stateId === "staged" || stateId === "before") {
      return [];
    }
    const solo = soloLabelKey(roleId);
    return keys
      .filter((k) => k === "line" || k === "seats" || k === "told")
      .concat(solo ? [solo] : []);
  }
  if (stateId === "residue") return [];
  // FT-1532 (ported): a chooser's face speaks its per-state pair — the white
  // line and the ask (a receive-only Lot's Sent ask is still its seal,
  // FT-1384)
  if (picksAnything(roleId) && !TOLD_ROLES[roleId]) {
    const face = {
      invite: "invite",
      reopened: "invite",
      staged: "picked",
      sealed: "sealed",
      "st-sent": "sent",
    }[stateId];
    if (!face) return [];
    const k = FACE_KEYS[face];
    const receiveOnly =
      nightExchange(roleId) === NIGHT_EXCHANGE.RECORDS && face === "sent";
    return [k.line, receiveOnly ? "sealed" : k.ask];
  }
  const beat = {
    invite: "invite",
    reopened: "invite",
    sealed: "sealed",
    telling: "telling",
    settled: "settled",
  }[stateId];
  return beat && keys.indexOf(beat) !== -1 ? ["line", beat] : ["line"];
}

/**
 * THE SHIPPED DEFAULT an empty box stands in for — resolved through the very
 * functions the surfaces fall back to, never a restatement of one. The told
 * keys have no default TEMPLATE (toldInfo composes the sentence), so their
 * placeholder is that composed sentence for the lab's own canned telling —
 * which is the sentence the box is about to replace on screen.
 */
export function copyDefaultFor(roleId, key) {
  const role = labRole(roleId);
  if (!role) return "";
  if (key === "telling" || key === "settled") {
    const { targets, told } = cannedTelling(roleId);
    const names = targets.map((t) => LAB_SEATS[t] || "");
    return toldSentence({ roleId, targets, ...told }, names, key === "settled");
  }
  const first = labDayOf(role) <= 1;
  // FT-1532 (ported): the white line in Invitation and Picked stands for the
  // Lot's own `line` (the editor's unsaved words included); in Confirmed and
  // Sent for no words at all
  if (key === "lineInvite" || key === "linePicked") {
    // FT-1536: the Lot's `line` left empty — these stand for no words too
    if (copyIsBlank(roleId, "line")) return "";
    return (
      overrideFor(roleId, "line") ||
      defaultCopyFor(roleId, "line", { isFirstNight: first }) ||
      reminderFor(role, first)
    );
  }
  if (key === "lineSealed" || key === "lineSent") return "";
  if (key === "line") {
    // the roster row's own expression: our line where one is written, the
    // shipped reminder where none is
    return (
      defaultCopyFor(roleId, "line", { isFirstNight: first }) ||
      reminderFor(role, first)
    );
  }
  return defaultCopyFor(roleId, key, { isFirstNight: first });
}

/**
 * ENTER. Snapshot everything the lab will touch, seed the fake town, seat
 * the viewer, fall into night. Returns false (and does nothing) in a town.
 */
export function enter(store, roleId = "monk", opts = {}) {
  if (!canEnter(store)) return false;
  // FT-1501: the Lot editor's door — `studio` says who drives, `role` is the
  // object for a Lot the bundle doesn't know
  extraRoles.clear();
  if (opts.role && roleId && !roleById.has(roleId)) {
    extraRoles.set(roleId, opts.role);
  }
  nightLab.studio = !!opts.studio;
  stopper = typeof opts.stop === "function" ? opts.stop : null;
  // the editor's night-text door, frozen so Vue never walks it
  nightLab.copy =
    opts.studio && opts.copy && typeof opts.copy.write === "function"
      ? Object.freeze(opts.copy)
      : null;
  const s = store.state;
  snap = {
    ls: LS_KEYS.map((k) => [k, localStorage.getItem(k)]),
    players: s.players.players,
    isSpectator: s.session.isSpectator,
    playerId: s.session.playerId,
    claimedSeat: s.session.claimedSeat,
    isNight: s.grimoire.isNight,
    night: {
      day: s.night.day,
      mode: s.night.mode,
      entries: s.night.entries,
      staged: s.night.staged,
      staging: {
        ...s.night.staging,
        targets: [...s.night.staging.targets],
        // FT-1532 (ported FT-1538): the staged character picks
        characters: [...(s.night.staging.characters || [])],
      },
      playerNight: {
        live: s.night.playerNight.live,
        rows: [...s.night.playerNight.rows],
      },
    },
  };
  labRowIds.clear();
  // spectator FIRST: with it on, the persistence plugin's host-only writers
  // (night mode / checklist) stay off for the whole visit
  store.commit("session/setSpectator", true);
  store.commit("night/setMode", "everyone");
  store.commit("players/set", []);
  LAB_SEATS.forEach((name) => store.commit("players/add", name));
  store.state.players.players.forEach((p, i) => {
    store.commit("players/update", {
      player: p,
      property: "id",
      value: LAB_IDS[i],
    });
  });
  store.commit("session/setPlayerId", LAB_IDS[ACTOR_SEAT]);
  store.commit("session/claimSeat", ACTOR_SEAT);
  nightLab.open = true;
  nightLab.perspective = "acting";
  setRole(store, labRole(roleId) ? roleId : "monk");
  return true;
}

/**
 * EXIT. Restore the store through ordinary commits (spectator restored LAST,
 * so the host-only persistence writers stay off until the store is already
 * back), then put every raw localStorage key back byte-for-byte.
 */
export function exit(store) {
  if (!nightLab.open || !snap) return;
  // 1. the told bookkeeping: every lab row re-arrives unsent — the reopen
  //    sweep clears its stamp — then the pre-entry frame is restored whole
  sweepLabRows(store);
  store.commit("night/setPlayerNight", {
    live: snap.night.playerNight.live,
    rows: snap.night.playerNight.rows,
  });
  // 2. phase before log: toggleNight's own day increment is then overwritten
  //    by the restored counter
  if (store.state.grimoire.isNight !== snap.isNight) {
    store.commit("toggleNight", snap.isNight);
  }
  store.commit("night/setLog", {
    day: snap.night.day,
    entries: snap.night.entries,
    staged: snap.night.staged,
  });
  store.commit("night/setMode", snap.night.mode);
  store.commit("night/resetStage");
  // 3. the town itself — the original player objects, untouched by the lab
  //    (every lab mutation ran against the fake seats)
  store.commit("players/set", snap.players);
  store.commit("session/claimSeat", snap.claimedSeat);
  store.commit("session/setPlayerId", snap.playerId);
  store.commit("session/setSpectator", snap.isSpectator);
  // 4. byte-exact storage: undo every intermediate write the persistence
  //    plugin made while the lab ran
  snap.ls.forEach(([k, v]) => {
    if (v === null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  });
  nightLab.open = false;
  nightLab.roleId = "";
  nightLab.stateId = "";
  nightLab.perspective = "acting";
  nightLab.studio = false;
  nightLab.copy = null;
  extraRoles.clear();
  labRowIds.clear();
  snap = null;
  stopper = null;
}

/**
 * FT-1493 → FT-1501: STOP WHATEVER PREVIEW IS RUNNING, from outside its own
 * controls (the menu's Exit preview). The owner's stop runs first when one
 * was handed in — the Lot editor's marks its preview stopped and drops its
 * draft words, exiting on its way — and the plain exit covers the rest.
 */
export function stopPreview(store) {
  if (!nightLab.open) return;
  const stop = stopper;
  if (stop) stop();
  if (nightLab.open) exit(store);
}
